/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import MomentService from 'cio/services/moment';

export interface BaseFormatTimestampArgs {
  timestamp: number;
  showTooltip?: boolean;
  capitalize?: boolean;
  truncateTooltip?: boolean;
  timestampPrefix?: string;
  timestampSuffix?: string;
  detailedTimestampPrefix?: string;
  detailedTimestampSuffix?: string;
  finalFormatter?: (timestampAsMoment: moment.Moment) => string;
  htmlSafeContent?: boolean;
  triggerClassNames?: string;
}

interface FormatTimestampArgs extends BaseFormatTimestampArgs {
  finalFormatter?: (timestampAsMoment: moment.Moment) => string;
}

export default class FormatTimestamp extends Component<FormatTimestampArgs> {
  @service moment!: MomentService;

  get showTooltip() {
    return this.args.showTooltip ?? true;
  }

  get capitalize() {
    return this.args.capitalize ?? true;
  }

  get truncateTooltip() {
    return this.args.truncateTooltip ?? false;
  }

  get timestampPrefix() {
    return this.args.timestampPrefix ?? '';
  }

  get timestampSuffix() {
    return this.args.timestampSuffix ?? '';
  }

  get detailedTimestampPrefix() {
    return this.args.detailedTimestampPrefix ?? '';
  }

  get detailedTimestampSuffix() {
    return this.args.detailedTimestampSuffix ?? '';
  }

  get finalFormatter() {
    return this.args.finalFormatter ?? this.defaultFinalFormatter;
  }

  get htmlSafeContent() {
    return this.args.htmlSafeContent ?? false;
  }

  get formattedTimestamp() {
    let { moment, capitalize, timestampPrefix, timestampSuffix } = this;

    let formattedTimestamp = moment.formatTimestamp([this.args.timestamp], {
      capitalize,
      finalFormatter: this.finalFormatter,
    });

    return `${timestampPrefix} ${formattedTimestamp} ${timestampSuffix}`.trim();
  }

  get detailedTimestamp() {
    if (!this.showTooltip) {
      return '';
    }

    let { moment, capitalize, detailedTimestampPrefix, detailedTimestampSuffix } = this;
    let timeText = moment.formatTimestamp([this.args.timestamp], { capitalize });
    return `${detailedTimestampPrefix} ${timeText} ${detailedTimestampSuffix}`.trim();
  }

  get needsTooltip() {
    return this.args.showTooltip === true || (this.showTooltip && this.detailedTimestamp !== this.formattedTimestamp);
  }

  @action
  defaultFinalFormatter(timestampAsMoment: moment.Moment) {
    return timestampAsMoment.format();
  }
}
